import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Axios from '@/shared/api/axios';
import { usePaginatedList } from '@/hooks/usePaginatedList';

import PageLayout from '@/components/layout/PageLayout';
import LeftSidebar from '@/components/layout/LeftSidebar';
import RightSidebar from '@/components/layout/RightSidebar';
import PostCard from '@/features/posts/components/PostCard';
import { getContentItemId } from '@/shared/utils/contentItemId';
import StatusModal from '@/components/ui/StatusModal';


const Search = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const q = searchParams.get('q');

  const idolId = useSelector((state) => state.user.idolId);

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  // 取得搜尋結果（單一頁），交由 usePaginatedList 處理分頁狀態與去重 append
  const fetchPage = async (pageNum) => {
    try {
      const res = await Axios.post("/search_v2", { page: pageNum, limit: 10, idol_id: idolId, keyword: q });
      if (res.data.status_code !== 'SYSTEM_1000') {
        setCurrentStatusCode(res.data.status_code);
        statusModalRef.current?.show();
        return null;
      }
      return { data: Object.values(res.data.data), total: res.data.total };
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Error fetching search results: ', error);
      }
      return null;
    }
  };

  // 搜尋結果每筆是 { post: {...} } 或 { world: {...} } 的包裝，去重 id 要從解包後的資料取得
  const getItemId = (item) => getContentItemId(item.post || item.world);

  const { data: list, setData: setList, isLoading, reset } = usePaginatedList({
    fetchPage,
    getId: getItemId,
    limit: 10,
  });

  // 點擊 tags 導向首頁並顯示相關貼文和世界
  const updateListWithTag = (tag) => {
    navigate(`/?tags=${tag}`);
  };

  // 本地更新/移除單一貼文或世界觀，取代整包 refetch（避免按讚/收藏/留言等高頻互動時列表跳動）
  // 搜尋結果每筆是 { post: {...} } 或 { world: {...} } 的包裝，updater 操作的是解包後的資料本身
  const patchItem = (itemId, updater) => {
    setList(prev => prev.reduce((acc, item) => {
      const key = item.post ? 'post' : 'world';
      const data = item[key];
      if (getContentItemId(data) !== itemId) {
        acc.push(item);
        return acc;
      }
      const next = updater(data);
      if (next) acc.push({ ...item, [key]: next });
      return acc;
    }, []));
  };

  useEffect(() => {
    reset();
  }, [])

  return (
    <PageLayout
      leftSidebar={<LeftSidebar />}
      rightSidebar={<RightSidebar />}
      isLoading={isLoading}
    >
      {list && list.length > 0 ? (
        list.map((item) => {
          if (item.post) {
            return (
              <PostCard
                key={`post-${item.post.post_info.post_id}`}
                data={item.post}
                contentType="post"
                onItemChange={(updater) => patchItem(getContentItemId(item.post), updater)}
                updateList={reset}
                onTagClick={updateListWithTag}
                showBadge={true}
              />
            );
          } else if (item.world) {
            return (
              <PostCard
                key={`world-${item.world.world_info.world_id}`}
                data={item.world}
                contentType="world"
                linkToDetail={true}
                onItemChange={(updater) => patchItem(getContentItemId(item.world), updater)}
                updateList={reset}
                onTagClick={updateListWithTag}
                showBadge={true}
              />
            );
          }
          return null;
        })
      ) : (
        <div className="rounded bg-white p-3 shadowA border text-center">{t('no_data')}</div>
      )}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </PageLayout>
  )
}

export default Search
